/* eslint-disable react/no-danger */
import { GetServerSideProps, NextPage } from 'next';
import Head from 'next/head';
import Script from 'next/script';

import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Seo from 'src/components/atoms/seo';
import config from 'src/config';

const CDN_PATH = '/business/b2b-landing-page';
const JOTFORM_EMBED_HANDLER_URL = 'https://cdn.jotfor.ms/s/umd/latest/for-form-embed-handler.js';

const ABSOLUTE_URL_RE = /^(?:[a-z][a-z0-9+.-]*:|\/\/|\/|#|data:|mailto:|tel:|javascript:)/i;
const DANGEROUS_URI_RE = /^\s*(?:javascript|vbscript):/i;

interface BusinessPageProps {
  content: string;
  cssUrl?: string;
  scriptUrl: string;
}

const BusinessPage: NextPage<BusinessPageProps> = ({ content, cssUrl, scriptUrl }) => {
  const { t } = useTranslation('business');

  return (
    <>
      <Seo
        title={t('seo_title')}
        description={t('seo_description')}
        image_url={`${config.IMAGE_CDN_HOST}/assets/b2b-landing/share-logo.jpg`}
      />
      <Head>{cssUrl && <link rel="stylesheet" href={cssUrl} />}</Head>
      <div dangerouslySetInnerHTML={{ __html: content }} />
      <Script src={JOTFORM_EMBED_HANDLER_URL} strategy="afterInteractive" />
      <Script src={scriptUrl} strategy="afterInteractive" />
    </>
  );
};

type Params = {
  locale: string;
};

function decodeUtf8Html(buffer: ArrayBuffer): string {
  return new TextDecoder('utf-8').decode(buffer);
}

function fixMojibake(content: string): string {
  if (!/à¸|Ã|Â/.test(content)) {
    return content;
  }

  try {
    const repaired = Buffer.from(content, 'latin1').toString('utf-8');
    return repaired.includes('�') ? content : repaired;
  } catch {
    return content;
  }
}

function extractBodyContent(html: string): string {
  const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  return bodyMatch ? bodyMatch[1] : html;
}

function rewriteAssetUrls(html: string, baseUrl: string): string {
  return html.replace(/(src|href|poster)="([^"]+)"/g, (match, attr, value) => {
    if (DANGEROUS_URI_RE.test(value)) {
      return `${attr}=""`;
    }
    if (ABSOLUTE_URL_RE.test(value)) {
      return match;
    }
    return `${attr}="${baseUrl}${value.replace(/^\.\//, '')}"`;
  });
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { locale } = context as Params;
  const baseUrl = `${config.IMAGE_CDN_HOST}${CDN_PATH}/`;
  const htmlUrl = `${baseUrl}index.html`;
  const cssUrl = `${baseUrl}styles.css`;
  const scriptUrl = `${baseUrl}scripts.js`;

  try {
    const [htmlResponse, cssResponse] = await Promise.all([fetch(htmlUrl), fetch(cssUrl, { method: 'HEAD' })]);

    if (!htmlResponse.ok) {
      return { notFound: true };
    }

    const htmlBuffer = await htmlResponse.arrayBuffer();
    let content = extractBodyContent(fixMojibake(decodeUtf8Html(htmlBuffer)));

    content = content
      .replace(/<!--[\s\S]*?-->/g, '')
      .replace(/<link\s+rel="stylesheet"[^>]*>/gi, '')
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');

    content = rewriteAssetUrls(content, baseUrl);

    const hasCss = cssResponse.ok;

    return {
      props: {
        content,
        cssUrl: hasCss ? cssUrl : null,
        scriptUrl,
        ...(await serverSideTranslations(locale, ['business', 'common'])),
      },
    };
  } catch {
    return { notFound: true };
  }
};

export default BusinessPage;
